/**
 * AIExplanationPane
 *
 * Right-side panel that shows AI explanation for how a selected field was extracted.
 * Displays:
 * - Field name and value
 * - AI reasoning/rationale
 * - Confidence score with visual indicator
 * - Source excerpts
 * - Citation thumbnails with links to full documents
 */
import {
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';

import { InspectArtifactModal } from '@/components/artifact-inspector/InspectArtifactModal';
import {
  EvidenceCard,
  EvidenceData,
} from '@/components/citations/EvidenceCard';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

export interface AIExtractionContext {
  /** Explanation of why this value was extracted */
  rationale?: string | null;
  /** Which artifact/document the value came from */
  extraction_source?: string | null;
  /** Notes about confidence level or extraction quality */
  confidence_notes?: string | null;
  /** ID of the artifact this value was extracted from */
  artifact_id?: string | null;
  /** Relevant text excerpts that support the extraction */
  excerpts?: string[] | null;
  /** Additional context about the extraction process */
  extraction_context?: string | null;
  /** Citations to specific locations in the source document */
  citation_ids?: string[] | null;
  /** Confidence score 0-1 */
  confidence?: number | null;
}

export interface AIExplanationPaneProps {
  /** Matter ID for API calls */
  matterId: string;
  /** Name of the selected field */
  fieldName?: string | null;
  /** Current value of the field */
  fieldValue?: string | null;
  /** Whether the field is required */
  isRequired?: boolean;
  /** AI extraction context for the field */
  extractionContext?: AIExtractionContext | null;
  /** Multiple extraction contexts if there are multiple sources */
  allExtractionContexts?: AIExtractionContext[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Whether the sources pane is frozen/locked to a specific field */
  isFrozen?: boolean;
}

/**
 * Confidence indicator with visual progress bar
 */
const ConfidenceIndicator: React.FC<{ confidence: number }> = ({
  confidence,
}) => {
  const percentage = Math.round(confidence * 100);
  const colorClass =
    confidence > 0.8
      ? 'text-green-700'
      : confidence > 0.6
        ? 'text-yellow-700'
        : 'text-red-700';
  const bgClass =
    confidence > 0.8
      ? '[&>div]:bg-green-500'
      : confidence > 0.6
        ? '[&>div]:bg-yellow-500'
        : '[&>div]:bg-red-500';
  const label = confidence > 0.8 ? 'High' : confidence > 0.6 ? 'Medium' : 'Low';
  const Icon = confidence > 0.8 ? CheckCircleIcon : ExclamationTriangleIcon;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Extraction Confidence
        </span>
        <div className="flex items-center gap-1.5">
          <Icon className={`h-4 w-4 ${colorClass}`} />
          <span className={`text-sm font-semibold ${colorClass}`}>
            {percentage}% ({label})
          </span>
        </div>
      </div>
      <Progress value={percentage} className={`h-2 ${bgClass}`} />
    </div>
  );
};

/**
 * Section for displaying AI reasoning
 */
const ReasoningSection: React.FC<{
  rationale?: string | null;
  extractionContext?: string | null;
}> = ({ rationale, extractionContext }) => {
  if (!rationale && !extractionContext) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <LightBulbIcon className="h-4 w-4 text-amber-500" />
        <span className="text-sm font-semibold text-gray-800">
          AI Reasoning
        </span>
      </div>
      <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100">
        {rationale && (
          <p className="text-sm text-gray-700 leading-relaxed">{rationale}</p>
        )}
        {extractionContext && (
          <p className="text-xs text-gray-500 mt-2 italic">
            {extractionContext}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Section for displaying source excerpts
 */
const ExcerptsSection: React.FC<{
  excerpts: string[];
  artifactId?: string | null;
}> = ({ excerpts, artifactId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (excerpts.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-semibold text-gray-800">
            Source Excerpts
          </span>
        </div>
        {artifactId && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            View full document
          </button>
        )}
      </div>
      <div className="space-y-2">
        {excerpts.map((excerpt, idx) => (
          <blockquote
            key={idx}
            className="text-sm text-gray-600 italic pl-3 border-l-2 border-blue-300 bg-blue-50/30 py-2 pr-3 rounded-r"
          >
            &ldquo;{excerpt}&rdquo;
          </blockquote>
        ))}
      </div>
      {artifactId && (
        <InspectArtifactModal
          artifactId={artifactId}
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      )}
    </div>
  );
};

/**
 * Empty state when no field is selected
 */
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8">
    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <SparklesIcon className="h-8 w-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-700 mb-2">
      AI Extraction Details
    </h3>
    <p className="text-sm text-gray-500 max-w-sm">
      Select a field on the left to see how the AI extracted the value,
      including source citations and confidence scores.
    </p>
  </div>
);

export const AIExplanationPane: React.FC<AIExplanationPaneProps> = ({
  matterId,
  fieldName,
  fieldValue,
  isRequired = false,
  extractionContext,
  allExtractionContexts = [],
  isLoading = false,
  isFrozen = false,
}) => {
  // Build evidence data from all contexts
  const evidenceItems: EvidenceData[] = useMemo(() => {
    const contexts =
      allExtractionContexts.length > 0
        ? allExtractionContexts
        : extractionContext
          ? [extractionContext]
          : [];

    return contexts
      .filter((ctx) => ctx.artifact_id || ctx.excerpts?.length)
      .map((ctx) => ({
        artifact_id: ctx.artifact_id,
        citation_ids: ctx.citation_ids,
        rationale: ctx.rationale,
        excerpts: ctx.excerpts,
        extraction_source: ctx.extraction_source,
        extraction_context: ctx.extraction_context,
        confidence_notes: ctx.confidence_notes,
        confidence: ctx.confidence,
      }));
  }, [extractionContext, allExtractionContexts]);

  // Get primary context for display
  const primaryContext = extractionContext || allExtractionContexts[0];
  const confidence = primaryContext?.confidence;
  const hasContent = Boolean(fieldName && (primaryContext || fieldValue));

  if (!hasContent && !isLoading) {
    return <EmptyState />;
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3" />
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-2/3" />
        <div className="h-20 bg-gray-200 rounded w-full" />
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-6">
        {/* Header: Field name and value */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50/80 to-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-gray-800">
                {fieldName}
              </CardTitle>
              <div className="flex items-center gap-2">
                {isFrozen && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-indigo-100 text-indigo-700 border-indigo-200"
                  >
                    🔒 Locked
                  </Badge>
                )}
                {isRequired && (
                  <Badge variant="outline" className="text-xs">
                    Required
                  </Badge>
                )}
                {primaryContext && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-purple-100 text-purple-700 border-purple-200"
                  >
                    <SparklesIcon className="h-3 w-3 mr-1" />
                    AI Extracted
                  </Badge>
                )}
              </div>
            </div>
            {isFrozen && (
              <p className="text-xs text-indigo-500 mt-1">
                Click the field again to unlock
              </p>
            )}
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium text-gray-900">
              {fieldValue || (
                <span className="text-gray-400 italic">No value</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Confidence indicator */}
        {confidence !== null && confidence !== undefined && (
          <ConfidenceIndicator confidence={confidence} />
        )}

        <Separator />

        {/* AI Reasoning */}
        <ReasoningSection
          rationale={primaryContext?.rationale}
          extractionContext={primaryContext?.extraction_context}
        />

        {/* Confidence notes */}
        {primaryContext?.confidence_notes && (
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            <p className="text-xs text-gray-600">
              <span className="font-medium">Note:</span>{' '}
              {primaryContext.confidence_notes}
            </p>
          </div>
        )}

        {/* Source excerpts */}
        {primaryContext?.excerpts && primaryContext.excerpts.length > 0 && (
          <ExcerptsSection
            excerpts={primaryContext.excerpts}
            artifactId={primaryContext.artifact_id}
          />
        )}

        <Separator />

        {/* Visual evidence cards */}
        {evidenceItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-800">
                Source Documents ({evidenceItems.length})
              </span>
            </div>
            <div className="space-y-4">
              {evidenceItems.map((evidence, idx) => (
                <EvidenceCard
                  key={(evidence.artifact_id || '') + idx}
                  evidence={evidence}
                  matterId={matterId}
                  variant="minimal"
                />
              ))}
            </div>
          </div>
        )}

        {/* No sources state */}
        {evidenceItems.length === 0 && primaryContext && (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              No source documents linked to this extraction.
            </p>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};
